import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import ConfirmationMessage from '@/common/components/blocks/Modal/Common/ConfirmationMessage/ConfirmationMessage';
import {
  CATEGORY_KEY_PARAM,
  MOVE_TYPE,
  NUMERIC_REGEX,
  ROUTE_WISHLISTS,
} from '@/common/constants';
import { useModalContext } from '@/common/contexts/ModalContext';
import { useTagManager } from '@/common/contexts/TagManagerContext';
import { useWishlistsContext } from '@/common/contexts/WishlistsContext';
import useQueryParams from '@/common/hooks/useQueryParams';
import { TAG_MANAGER, WISHLISTS_TYPES } from '@/ebat/constants';

const useWishlists = ({ getParams, isLoading, onRefetch, setParams, tree }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const getQueryParams = useQueryParams();
  const {
    setModalContent,
    setModalClassName,
    setModalSize,
    onOpenModal,
  } = useModalContext();
  const { onPushEvent } = useTagManager();

  /**
   * Checks if category is defined in query strings and returns otherwise returns null
   */
  const getCategoryFromQueryString = useMemo(
    () => getQueryParams.get(CATEGORY_KEY_PARAM) * 1 || null,
    [getQueryParams],
  );

  const {
    isLoading: isLoadingFromContext,
    onAddCategory,
    onAddWishlist,
    onMoveCategory,
    onMoveSection,
    onRemoveCategory,
    onUpdateCategory,
  } = useWishlistsContext();

  const [localTree, setLocalTree] = useState(tree);

  const customSort = useCallback((array) => {
    // Trie d'abord par nom
    array.sort((a, b) => a.name.localeCompare(b.name));

    // Trie ensuite par numéro uniquement pour les éléments ayant un numéro
    const itemsWithNumbers = array.filter((item) =>
      NUMERIC_REGEX.test(item.name),
    );

    itemsWithNumbers.sort((a, b) => {
      const numA = parseInt(NUMERIC_REGEX.exec(a.name)[1], 10);
      const numB = parseInt(NUMERIC_REGEX.exec(b.name)[1], 10);
      return numA - numB;
    });

    // Réinsère les éléments triés avec numéro dans leur position alphabétique
    const newArray = [...array];
    let j = 0;

    for (let i = 0; i < newArray.length; i += 1) {
      if (NUMERIC_REGEX.test(newArray[i].name)) {
        newArray[i] = itemsWithNumbers[j];
        j += 1;
      }
    }

    return newArray;
  }, []);

  const sortCategory = useCallback(
    (category) => {
      const sortedCategory = { ...category };

      sortedCategory.wishlists = customSort(sortedCategory.wishlists);
      sortedCategory.children = customSort(
        sortedCategory.children.map(sortCategory),
      );

      return sortedCategory;
    },
    [customSort],
  );

  const sortWishlistsData = useMemo(() => {
    if (isLoading && !tree) {
      return {
        orphan_wishlists: [],
        categories: [],
      };
    }

    const sortedData = { ...tree };

    sortedData.categories = customSort(sortedData.categories.map(sortCategory));
    sortedData.orphan_wishlists = customSort(sortedData.orphan_wishlists);

    return sortedData;
  }, [customSort, isLoading, sortCategory, tree]);

  /**
   * Set category id to localCategory
   * @param {object} category: category with id or null
   */
  const onSelectCategory = useCallback(
    (category) => {
      setParams({ [CATEGORY_KEY_PARAM]: category?.id });
    },
    [setParams],
  );

  /**
   * Update tree on state
   * @param {object} newTree updated tree
   * @type {function(*=): void}
   */
  const onUpdateTree = useCallback((newTree) => {
    setLocalTree(newTree);
  }, []);

  const isDisabled = useMemo(() => {
    const loading = isLoading || isLoadingFromContext;

    return {
      disabled: loading,
      loading,
    };
  }, [isLoading, isLoadingFromContext]);

  /**
   * Length of wishlists
   * @type {number}
   */
  const wishlistsLength = useMemo(() => localTree?.wishlists?.length, [
    localTree,
  ]);

  /**
   * Redirect to wishlist
   * @param {object} data from context addWishlist on the useWishlistsContext
   */
  const redirectToWishlist = useCallback(
    (data) => {
      const { id } = data;

      history.push(`${ROUTE_WISHLISTS}/${id}`);
    },
    [history],
  );

  /**
   * Push dataLayer
   * @type {function(): void}
   */
  const handlePushDataLayer = useCallback(
    () => onPushEvent({ event: TAG_MANAGER.EVENTS.CTA_ADD_WISHLIST }),
    [onPushEvent],
  );

  /**
   * Actions on add wishlist success
   * @type {(...args: any[]) => any}
   * @param newWishlist {object}
   */
  const handleAddWishlistSuccess = useCallback(
    (newWishlist) => {
      redirectToWishlist(newWishlist);
      handlePushDataLayer();
    },
    [handlePushDataLayer, redirectToWishlist],
  );

  /**
   * Create new wishlist
   * @param {number} categoryId If there is a parent
   */
  const handleAddWishlist = useCallback(
    (categoryId) => {
      const defaultName = categoryId
        ? t('ebat:entities:wishlists.actions.add_wishlist', {
            rank:
              localTree.categories.find((cat) => cat.id === categoryId).children
                .length + 1,
          })
        : t('ebat:entities:wishlists.actions.add_wishlist', {
            rank: localTree.categories.length + 1,
          });
      onAddWishlist({
        name: defaultName,
        categoryId,
        onSuccess: handleAddWishlistSuccess,
      });
    },
    [t, localTree, onAddWishlist, handleAddWishlistSuccess],
  );

  /**
   * Updated position in the section of the category
   * @param {object} item: category (category or section)
   * @param {string} type: type category or section
   * @param {function} onError
   * @param {function} onSuccess
   */
  const handleUpdateCategory = useCallback(
    ({ item, type, onError = () => {}, onSuccess = () => {} }) => {
      onUpdateCategory({
        item,
        type,
        onError,
        onSuccess: (data) => {
          onSuccess(data);

          switch (type) {
            case WISHLISTS_TYPES.CATEGORY:
              onUpdateTree({
                ...localTree,
                categories: localTree.categories.map((cat) =>
                  cat.id === data.id ? data : cat,
                ),
              });
              break;
            case WISHLISTS_TYPES.SECTION:
              onUpdateTree({
                ...localTree,
                categories: localTree.categories.map((cat) =>
                  cat.id === getCategoryFromQueryString * 1
                    ? {
                        ...cat,
                        children: cat.children.map((section) =>
                          section.id === item.id ? data : section,
                        ),
                      }
                    : { ...cat },
                ),
              });
              break;
            default:
              onUpdateTree({ ...localTree });
          }
        },
      });
    },
    [getCategoryFromQueryString, localTree, onUpdateCategory, onUpdateTree],
  );

  /**
   * Add category or section with the parent id
   * @param {number} id category parent
   */
  const handleAddCategory = useCallback(
    (id) => {
      let params = {
        name: id
          ? t('ebat:entities:wishlists.actions.add_section', {
              rank:
                localTree.categories.find((cat) => cat.id === id).children
                  .length + 1,
            })
          : t('ebat:entities:wishlists.actions.add_category', {
              rank: localTree.categories.length + 1,
            }),
        onSuccess: onUpdateTree,
      };
      if (id) {
        params = {
          ...params,
          id,
        };
      }
      onAddCategory({
        ...params,
      });
    },
    [localTree, onAddCategory, onUpdateTree, t],
  );

  /**
   * Moves the section according to the up or down movement type.
   * @param {string} type
   * @param {number} id
   * @param {number} parentId
   * @param {number} position
   * @type {(function({type: string, id: number, parentId: number, position: number}): void)|*}
   */
  const handleMoveSection = useCallback(
    ({ type, id, parentId, position }) => {
      switch (type) {
        case MOVE_TYPE.UP:
          onMoveSection({
            section: { id, parentId, position: position - 1 },
            onSuccess: onUpdateTree,
          });
          break;
        case MOVE_TYPE.DOWN:
          onMoveSection({
            section: { id, parentId, position: position + 1 },
            onSuccess: onUpdateTree,
          });
          break;
        default:
          break;
      }
    },
    [onMoveSection, onUpdateTree],
  );

  /**
   * Get the information from the wishlist item and the parent category and moves the wishlist
   * @param {object} item get the item of the wishlist
   * @param {object} category get parent category
   * @type {(function({item: object, category: object}): void)|*}
   */
  const handleMoveCategory = useCallback(
    ({ item, category }) => {
      if (category.id) {
        onMoveCategory({
          item,
          category,
          onSuccess: onUpdateTree,
        });
      }
      return null;
    },
    [onMoveCategory, onUpdateTree],
  );

  /**
   * Get the current category if exists
   * @type {object}
   */
  const getCurrentCategory = useMemo(
    () =>
      localTree?.categories.find(
        (item) =>
          item.id === parseInt(getParams && getParams(CATEGORY_KEY_PARAM), 10),
      ) || null,
    [getParams, localTree],
  );

  /**
   * Deleted item category
   * @param {object} item category
   */
  const handleRemoveCategory = useCallback(
    (item) => {
      onRemoveCategory({
        item,
        onSuccess: onRefetch,
      });
      onSelectCategory(null);
    },
    [onRefetch, onRemoveCategory, onSelectCategory],
  );

  /**
   * Deleted item section
   * @param {object} item section
   */
  const handleRemoveSection = useCallback(
    (item) => {
      onRemoveCategory({
        item,
        onSuccess: onRefetch,
      });
    },
    [onRefetch, onRemoveCategory],
  );

  /**
   * Open modal for confirmation Message
   * @param {object} item category selected
   * @param {boolean} isCategory if category
   */
  const handleOpenModal = useCallback(
    ({ item, type }) => {
      setModalContent(
        <ConfirmationMessage
          onSubmit={() => {
            switch (type) {
              case WISHLISTS_TYPES.CATEGORY:
                handleRemoveCategory(item);
                break;
              case WISHLISTS_TYPES.SECTION:
                handleRemoveSection(item);
                break;
              default:
                break;
            }
          }}
        />,
      );
      setModalClassName('modal-confirmation-message');
      setModalSize('sm');
      onOpenModal();
    },
    [
      handleRemoveCategory,
      handleRemoveSection,
      onOpenModal,
      setModalClassName,
      setModalContent,
      setModalSize,
    ],
  );

  // Use effect generate a side effect when tree is changing
  useEffect(() => {
    setLocalTree(tree);
  }, [tree]);

  return {
    getCurrentCategory,
    isDisabled,
    localTree,
    onAddCategory: handleAddCategory,
    onAddWishlist: handleAddWishlist,
    onMoveCategory: handleMoveCategory,
    onMoveSection: handleMoveSection,
    onOpenModal: handleOpenModal,
    onRemoveCategory: handleRemoveCategory,
    onSelectCategory,
    onUpdateCategory: handleUpdateCategory,
    onUpdateTree,
    sortWishlistsData,
    wishlistsLength,
  };
};

export default useWishlists;
